import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocNestedBlock, DocTwoColumnBlock } from "@/lib/document-builder/blocks/blockTypes";
import { DOC_NESTED_BLOCK_TYPES } from "@/lib/document-builder/blocks/blockNested";
import { DOC_BLOCK_TYPE_LABELS } from "@/lib/document-builder/blocks/blockTypes";
import { createDocNestedBlock } from "@/lib/document-builder/blocks/blockDefaults";
import { sortDocBlocks, updateDocBlock } from "@/lib/document-builder/blocks/blockReorder";
import { DocumentBlockFields } from "./DocumentBlockFields";

type ColumnSide = "left" | "right";

type Props = {
  block: DocTwoColumnBlock;
  assets: DocAsset[];
  onChange: (block: DocTwoColumnBlock) => void;
};

export function DocumentTwoColumnFields({ block, assets, onChange }: Props) {
  function updateColumn(side: ColumnSide, blocks: DocNestedBlock[]) {
    onChange({ ...block, [side]: sortDocBlocks(blocks) as DocNestedBlock[] });
  }

  function addNested(side: ColumnSide, type: (typeof DOC_NESTED_BLOCK_TYPES)[number]) {
    const col = block[side];
    updateColumn(side, [...col, createDocNestedBlock(type, col.length)]);
  }

  function patchNested(side: ColumnSide, updated: DocNestedBlock) {
    updateColumn(side, updateDocBlock(block[side], updated) as DocNestedBlock[]);
  }

  function removeNested(side: ColumnSide, blockId: string) {
    const next = block[side].filter((b) => b.id !== blockId);
    updateColumn(side, next.length > 0 ? next : [createDocNestedBlock("paragraph", 0)]);
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {(["left", "right"] as const).map((side) => (
        <ColumnEditor
          key={side}
          title={side === "left" ? "Left column" : "Right column"}
          blocks={block[side]}
          assets={assets}
          onAdd={(type) => addNested(side, type)}
          onChange={(b) => patchNested(side, b)}
          onRemove={(id) => removeNested(side, id)}
        />
      ))}
    </div>
  );
}

function ColumnEditor(props: {
  title: string;
  blocks: DocNestedBlock[];
  assets: DocAsset[];
  onAdd: (type: (typeof DOC_NESTED_BLOCK_TYPES)[number]) => void;
  onChange: (block: DocNestedBlock) => void;
  onRemove: (blockId: string) => void;
}) {
  const { title, blocks, assets, onAdd, onChange, onRemove } = props;
  const sorted = sortDocBlocks(blocks) as DocNestedBlock[];

  return (
    <div className="rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
      <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-zinc-300">{title}</p>
      <div className="mb-2 flex flex-wrap gap-1">
        {DOC_NESTED_BLOCK_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onAdd(type)}
            className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
          >
            + {DOC_BLOCK_TYPE_LABELS[type]}
          </button>
        ))}
      </div>
      <ul className="space-y-2">
        {sorted.map((nested) => (
          <li key={nested.id} className="rounded border border-gray-100 p-2 dark:border-zinc-800">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase text-gray-400">{DOC_BLOCK_TYPE_LABELS[nested.type]}</span>
              <button
                type="button"
                onClick={() => onRemove(nested.id)}
                className="text-[10px] text-red-600 dark:text-red-400"
              >
                Remove
              </button>
            </div>
            <DocumentBlockFields
              block={nested}
              assets={assets}
              onChange={(b) => onChange(b as DocNestedBlock)}
              onRichTextWrap={() => {}}
              onRichTextPrefix={() => {}}
              onRichTextLink={() => {}}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
